import { useCallback, useRef, useState } from 'react';

/**
 * A custom hook for debouncing a function call.
 *
 * @returns {[function, boolean]} A debounced function and a boolean indicating whether the bounce is pending.
 * @param {number} timeout - The debounce timeout in milliseconds.
 */
export const useDebounce = (timeout = 500) => {
  const [pending, setPending] = useState(false);
  const handler = useRef(null); // No need to trigger re-renders

  /**
   * Debounces a function call.
   *
   * @param {function} fn - The function to be debounced.
   * @param {number} [timeoutOverride] - Optional override for timeout.
   */
  const debounceFn = useCallback(
    (fn, timeoutOverride = timeout) => {
      clearTimeout(handler.current);
      setPending(true);
      handler.current = setTimeout(() => {
        fn();
        setPending(false);
      }, timeoutOverride);
    },
    [timeout],
  );

  return [debounceFn, pending];
};
